import Phaser from 'phaser';
import { Enemy } from '../Enemy';
import { WALL_X, WALL_WIDTH } from '../../config';
import { ChainUpgradeEffect, TargetPriority } from '../../types';

export abstract class BaseUnit {
  scene: Phaser.Scene;
  x: number;
  y: number;
  level: number;
  slotIndex: number;
  graphics: Phaser.GameObjects.Graphics;

  sprite: Phaser.GameObjects.Sprite;

  // Range boost multiplier (set externally by GameScene from EconomyManager)
  rangeBoostMultiplier: number = 1;

  // Stack count - each additional "unit" bought adds to this, increasing damage
  stackCount: number = 1;

  // Global damage multiplier (from upgrade + prestige, set each frame by GameScene)
  globalDamageMult: number = 1;

  // Global attack speed multiplier (from prestige elder, set each frame by GameScene)
  attackSpeedMult: number = 1;

  // Targeting priority
  targetPriority: TargetPriority = 'nearest';

  // ---- Chain upgrade accumulated bonuses ----
  bonusDamageMult: number = 1;
  bonusProjectiles: number = 0;
  attackIntervalMult: number = 1;
  bonusPierceCount: number = 0;
  bonusAoeRadius: number = 0;
  bonusMaxTargets: number = 0;
  repairRateMult: number = 1;
  repairIntervalMult: number = 1;
  accuracyPenalty: number = 0.5;
  poisonPercent: number = 0; // Fraction of arrow damage dealt as poison per tick
  poisonDuration: number = 0;
  poisonEnabled: boolean = false;
  firePercent: number = 0; // Fraction of damage dealt as fire per tick
  fireDuration: number = 0;
  fireEnabled: boolean = false;
  bonusSlowEffect: number = 0;
  bonusSlowDuration: number = 0;
  doubleShot: boolean = false;
  stunDuration: number = 0;
  knockbackDistance: number = 0;
  disarmDuration: number = 0;
  rangeBonus: number = 0;

  // Boss status effects
  frozenTimer: number = 0;    // Frost Warlord: unit can't attack while > 0
  stunnedTimer: number = 0;   // Storm Herald: unit can't attack while > 0
  plagueTimer: number = 0;    // Plague Shaman: unit has reduced attack speed while > 0

  constructor(scene: Phaser.Scene, x: number, y: number, slotIndex: number) {
    this.scene = scene;
    this.x = x;
    this.y = y;
    this.level = 1;
    this.slotIndex = slotIndex;
    this.stackCount = 1;
    this.graphics = scene.add.graphics();
    this.sprite = scene.add.sprite(x, y, 'friendly_rock_idle'); // Default placeholder
    this.sprite.setOrigin(0.5, 110 / 128);
  }

  abstract update(time: number, delta: number, enemies: Enemy[]): void;
  abstract draw(): void;

  /** Get effective attack interval including chain upgrades and prestige attack speed */
  getEffectiveAttackInterval(baseInterval: number): number {
    return baseInterval * this.attackIntervalMult / this.attackSpeedMult;
  }

  applyChainUpgrade(effect: ChainUpgradeEffect): void {
    if (effect.damageMultBonus !== undefined) this.bonusDamageMult *= (1 + effect.damageMultBonus);
    if (effect.extraProjectiles !== undefined) this.bonusProjectiles += effect.extraProjectiles;
    if (effect.attackIntervalMult !== undefined) this.attackIntervalMult *= effect.attackIntervalMult;
    if (effect.aoeRadiusBonus !== undefined) this.bonusAoeRadius += effect.aoeRadiusBonus;
    if (effect.maxTargetsBonus !== undefined) this.bonusMaxTargets += effect.maxTargetsBonus;
    if (effect.pierceCountBonus !== undefined) this.bonusPierceCount += effect.pierceCountBonus;
    if (effect.repairRateMult !== undefined) this.repairRateMult *= effect.repairRateMult;
    if (effect.repairIntervalMult !== undefined) this.repairIntervalMult *= effect.repairIntervalMult;
    if (effect.accuracyPenaltyMult !== undefined) this.accuracyPenalty *= effect.accuracyPenaltyMult;
    if (effect.accuracyPenaltySet !== undefined) this.accuracyPenalty = effect.accuracyPenaltySet;
    if (effect.poisonEnabled) this.poisonEnabled = true;
    if (effect.poisonPercentBonus !== undefined) this.poisonPercent += effect.poisonPercentBonus;
    if (effect.poisonDurationBonus !== undefined) this.poisonDuration += effect.poisonDurationBonus;
    if (effect.fireEnabled) this.fireEnabled = true;
    if (effect.firePercentBonus !== undefined) this.firePercent += effect.firePercentBonus;
    if (effect.fireDurationBonus !== undefined) this.fireDuration += effect.fireDurationBonus;
    if (effect.slowEffectBonus !== undefined) this.bonusSlowEffect += effect.slowEffectBonus;
    if (effect.slowDurationBonus !== undefined) this.bonusSlowDuration += effect.slowDurationBonus;
    if (effect.doubleShot) this.doubleShot = true;
    if (effect.stunDuration !== undefined) this.stunDuration += effect.stunDuration;
    if (effect.knockbackDistance !== undefined) this.knockbackDistance += effect.knockbackDistance;
    if (effect.disarmDuration !== undefined) this.disarmDuration += effect.disarmDuration;
    if (effect.rangeBonus !== undefined) this.rangeBonus += effect.rangeBonus;
    this.level++;
    this.draw();
  }

  get pierceCount(): number {
    return this.bonusPierceCount; // Base units don't have basePierceCount property unless added.
    // Spec: Projectile uses maxPierceCount.
    // If unit has base pierce, it should be in config or property.
    // I'll assume 0 base for now or allow subclasses to override.
  }

  // Add another "unit" to this stack - increases damage
  addStack(): void {
    this.stackCount++;
  }

  // Get damage multiplier from stacking
  getStackMultiplier(): number {
    return this.stackCount;
  }

  // Range measured horizontally from the wall (for wall-slot units).
  findNearestEnemy(enemies: Enemy[], range: number): Enemy | null {
    let nearest: Enemy | null = null;
    const effectiveRange = range * this.rangeBoostMultiplier;
    const wallEdge = WALL_X + WALL_WIDTH;
    let minDist = Infinity;

    for (const enemy of enemies) {
      if (enemy.isDead) continue;
      if (enemy.enemyType === 'boulder') continue;
      if (enemy.isBurrowed) continue;
      if (enemy.isGoblin) continue;
      const horizontalDist = enemy.x - wallEdge;
      if (horizontalDist > effectiveRange) continue;

      const dx = enemy.x - this.x;
      const dy = enemy.y - this.y;
      const dist = Math.sqrt(dx * dx + dy * dy);
      if (dist < minDist) {
        minDist = dist;
        nearest = enemy;
      }
    }
    return nearest;
  }

  findTarget(enemies: Enemy[], range: number): Enemy | null {
    if (this.targetPriority === 'nearest') {
      return this.findNearestEnemy(enemies, range);
    }
    // Gather in-range candidates
    const effectiveRange = range * this.rangeBoostMultiplier;
    const wallEdge = WALL_X + WALL_WIDTH;
    const candidates: Enemy[] = [];
    for (const enemy of enemies) {
      if (enemy.isDead) continue;
      if (enemy.enemyType === 'boulder') continue;
      if (enemy.isBurrowed) continue;
      if (enemy.isGoblin) continue;
      if (enemy.x - wallEdge > effectiveRange) continue;
      candidates.push(enemy);
    }
    if (candidates.length === 0) return null;
    return selectByPriority(candidates, this.targetPriority, this.x, this.y);
  }

  destroy(): void {
    this.graphics.destroy();
    if (this.sprite) {
      this.sprite.destroy();
    }
  }
}

/** Standalone target selection helper for buildings and other non-BaseUnit entities */
export function selectByPriority(candidates: Enemy[], priority: TargetPriority, refX: number, refY: number): Enemy | null {
  if (candidates.length === 0) return null;

  switch (priority) {
    case 'nearest': {
      let best: Enemy | null = null;
      let minDist = Infinity;
      for (const e of candidates) {
        const dx = e.x - refX;
        const dy = e.y - refY;
        const dist = dx * dx + dy * dy;
        if (dist < minDist) { minDist = dist; best = e; }
      }
      return best;
    }
    case 'lowestHP': {
      let best: Enemy | null = null;
      let minHP = Infinity;
      for (const e of candidates) {
        if (e.hp < minHP) { minHP = e.hp; best = e; }
      }
      return best;
    }
    case 'highestHP': {
      let best: Enemy | null = null;
      let maxHP = -1;
      for (const e of candidates) {
        if (e.hp > maxHP) { maxHP = e.hp; best = e; }
      }
      return best;
    }
    case 'fastest': {
      let best: Enemy | null = null;
      let maxSpeed = -1;
      for (const e of candidates) {
        if (e.baseSpeed > maxSpeed) { maxSpeed = e.baseSpeed; best = e; }
      }
      return best;
    }
    case 'backline': {
      // Prefer shamans/archers (support/ranged types), then furthest from wall
      const backlineTypes = ['shaman', 'archer', 'longbow_archer'];
      const priorityTargets = candidates.filter(e => backlineTypes.includes(e.enemyType));
      const pool = priorityTargets.length > 0 ? priorityTargets : candidates;
      let best: Enemy | null = null;
      let maxX = -Infinity;
      for (const e of pool) {
        if (e.x > maxX) { maxX = e.x; best = e; }
      }
      return best;
    }
    case 'mostClustered': {
      let best: Enemy | null = null;
      let maxNeighbors = -1;
      const clusterRadius = 60;
      const r2 = clusterRadius * clusterRadius;
      for (const e of candidates) {
        let neighbors = 0;
        for (const other of candidates) {
          if (other === e) continue;
          const dx = other.x - e.x;
          const dy = other.y - e.y;
          if (dx * dx + dy * dy <= r2) neighbors++;
        }
        if (neighbors > maxNeighbors) { maxNeighbors = neighbors; best = e; }
      }
      return best;
    }
    default:
      return candidates[0];
  }
}
